import React, { useMemo, useCallback } from 'react';
import { useLazyQuery } from '@apollo/client';
import { GenericFilterHeader } from './GenericFilterHeader';
import { GET_PROCUREMENT_MANAGERS_FILTER } from '../../../../apollo/queries/procurementManager';
import { ProcurementManager } from '../../../../codegen/graphql';

interface ProcurementManagerColumnHeaderProps {
    currentValue: string[] | null;
    onChange: (value: string[] | null) => void;
}

type ProcurementManagersFilterResult = {
    getAllProcurementManagers: ProcurementManager[];
};

export const ProcurementManagerColumnHeader: React.FC<ProcurementManagerColumnHeaderProps> =
    React.memo(({ currentValue, onChange }) => {
        const [fetchManagers, { data, loading }] =
            useLazyQuery<ProcurementManagersFilterResult>(
                GET_PROCUREMENT_MANAGERS_FILTER,
                { fetchPolicy: 'cache-first' },
            );

        const handleOpen = useCallback(() => {
            fetchManagers();
        }, [fetchManagers]);

        const procurementManagersData = useMemo(() => {
            if (!data) return [];
            return data.getAllProcurementManagers
                .filter(
                    pm =>
                        pm.manager?.id &&
                        pm.manager?.firstName &&
                        pm.manager?.lastName,
                )
                .map(pm => ({
                    id: pm.manager!.id,
                    name: `${pm.manager!.firstName} ${pm.manager!.lastName}`,
                }));
        }, [data]);

        const filterOptions = useMemo(() => {
            return ['All', ...procurementManagersData.map(pm => pm.name)];
        }, [procurementManagersData]);

        const handleFilterChange = useCallback(
            (selectedNames: string[] | null) => {
                if (!selectedNames) {
                    onChange(null);
                    return;
                }
                if (selectedNames.includes('All')) {
                    onChange(null);
                    return;
                }
                const selectedIds: string[] = [];
                if (selectedNames.includes('Unassigned')) {
                    selectedIds.push('Unassigned');
                }
                procurementManagersData.forEach(pm => {
                    if (selectedNames.includes(pm.name)) {
                        selectedIds.push(pm.id);
                    }
                });
                onChange(selectedIds.length > 0 ? selectedIds : null);
            },
            [onChange, procurementManagersData],
        );

        const displayedCurrentValue = useMemo(() => {
            if (!currentValue) return null;
            const names: string[] = [];
            if (currentValue.includes('Unassigned')) {
                names.push('Unassigned');
            }
            procurementManagersData.forEach(pm => {
                if (currentValue.includes(pm.id)) {
                    names.push(pm.name);
                }
            });
            return names.length > 0 ? names : null;
        }, [currentValue, procurementManagersData]);

        return (
            <GenericFilterHeader
                label="PM's"
                filterOptions={filterOptions}
                currentValue={currentValue}
                onChange={handleFilterChange}
                displayValue={displayedCurrentValue}
                onOpen={handleOpen}
                loading={loading}
            />
        );
    });

ProcurementManagerColumnHeader.displayName = 'ProcurementManagerColumnHeader';
